import {
  getCopilotRouteOptionsQuery,
  workspaceByokSettingsQuery,
} from '@affine/graphql';
import { signal } from '@preact/signals-core';
import { Service } from '@toeverything/infra';

import type { GraphQLService, SubscriptionService } from '../../cloud';
import type { GlobalStateService } from '../../storage';

const ROUTE_TARGET_KEY = 'AIManagedRouteTarget';

export interface AIModel {
  id: string;
  name: string;
  category: string;
  version: string;
  available: boolean;
  profileId?: string;
  modelId?: string;
}

export type AIModelSelection =
  | { routeTargetId?: undefined; profileId?: undefined; modelId?: undefined }
  | { routeTargetId: string; profileId?: undefined; modelId?: undefined }
  | { routeTargetId?: undefined; profileId: string; modelId: string };

const isPermissionDenied = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'isStatus' in error &&
  typeof error.isStatus === 'function' &&
  error.isStatus(403);

const supportsText = (capability: { input: string[]; output: string[] }) =>
  capability.input.includes('text') && capability.output.includes('text');

export class AIModelService extends Service {
  readonly modelId = signal<string | undefined>(undefined);
  readonly models = signal<AIModel[]>([]);

  private workspaceId: string | undefined;
  private routeId: string | undefined;
  private requestId = 0;

  constructor(
    private readonly globalStateService: GlobalStateService,
    private readonly gqlService: GraphQLService,
    subscriptionService: SubscriptionService
  ) {
    super();
    const subscription = subscriptionService.subscription.ai$.subscribe(() => {
      if (this.workspaceId && this.routeId) {
        this.load(this.workspaceId, this.routeId).catch(() =>
          console.error('Failed to load AI models')
        );
      }
    });
    this.disposables.push(() => subscription.unsubscribe());
  }

  setScope(workspaceId: string, routeId: string) {
    if (workspaceId === this.workspaceId && routeId === this.routeId) return;
    this.workspaceId = workspaceId;
    this.routeId = routeId;
    this.models.value = [];
    this.modelId.value = undefined;
    this.load(workspaceId, routeId).catch(() =>
      console.error('Failed to load AI models')
    );
  }

  resetModel() {
    this.setModel(undefined);
  }

  getSelection(): AIModelSelection {
    const model = this.models.value.find(
      candidate => candidate.id === this.modelId.value && candidate.available
    );
    if (!model) return {};
    if (model.profileId && model.modelId) {
      return { profileId: model.profileId, modelId: model.modelId };
    }
    return { routeTargetId: model.id };
  }

  setModel(modelId: string | undefined) {
    if (!this.workspaceId || !this.routeId) return;
    if (
      modelId &&
      !this.models.value.find(model => model.id === modelId)?.available
    ) {
      return;
    }
    this.modelId.value = modelId;
    this.globalStateService.globalState.set(
      this.storageKey(this.workspaceId, this.routeId),
      modelId
    );
  }

  private async load(workspaceId: string, routeId: string) {
    const requestId = ++this.requestId;
    const to = new Date();
    const from = new Date(to.getTime());
    from.setDate(from.getDate() - 30);
    const [managedResponse, byokResponse] = await Promise.allSettled([
      this.gqlService.gql({
        query: getCopilotRouteOptionsQuery,
        variables: { promptName: routeId },
      }),
      this.gqlService
        .gql({
          query: workspaceByokSettingsQuery,
          variables: {
            id: workspaceId,
            from: from.toISOString(),
            to: to.toISOString(),
          },
        })
        .catch(error => {
          if (isPermissionDenied(error)) return undefined;
          throw error;
        }),
    ]);
    if (requestId !== this.requestId) return;
    if (byokResponse.status === 'rejected') {
      console.error('Failed to load workspace BYOK models');
      throw byokResponse.reason;
    }
    if (managedResponse.status === 'rejected') {
      console.error('Failed to load managed AI models');
    }
    const managedResult =
      managedResponse.status === 'fulfilled' ? managedResponse.value : undefined;
    const byokResult =
      byokResponse.status === 'fulfilled' ? byokResponse.value : undefined;
    const options = managedResult?.currentUser?.copilot?.routeOptions;
    const managedModels = options?.choices.map(choice => {
      const [category] = choice.displayName.split(' ');
      return {
        id: choice.id,
        name: choice.displayName,
        category,
        version: choice.displayName.slice(category.length + 1),
        available: choice.available,
      };
    }) ?? [];
    const settings = byokResult?.workspace?.byokSettings;
    const byokModels =
      settings?.serverEntitled && settings.policy.enabled
        ? settings.profiles.flatMap(profile => {
            if (
              !profile.enabled ||
              !settings.policy.allowedProviders.includes(profile.provider)
            ) {
              return [];
            }
            return profile.definition.models
              .filter(model => model.enabled && model.capabilities.some(supportsText))
              .map(model => ({
                id: `byok:${profile.profileId}:${encodeURIComponent(model.modelId)}`,
                name: model.modelId,
                category: model.modelId,
                version: profile.name,
                available: true,
                profileId: profile.profileId,
                modelId: model.modelId,
              }));
          })
        : [];
    this.models.value = [...managedModels, ...byokModels];
    const selected = this.globalStateService.globalState.get<string>(
      this.storageKey(workspaceId, routeId)
    );
    const selectedAvailable = this.models.value.some(
      model => model.id === selected && model.available
    );
    this.modelId.value = selectedAvailable ? selected : undefined;
    if (selected && !selectedAvailable) {
      this.globalStateService.globalState.set(
        this.storageKey(workspaceId, routeId),
        undefined
      );
    }
  }

  private storageKey(workspaceId: string, routeId: string) {
    return `${ROUTE_TARGET_KEY}:${workspaceId}:${routeId}`;
  }
}
